import { ltjsModule, typeMap } from './ltjsEnvironment';
import { traceEnabled } from './util/TraceUtil';
//
// JavaScript uses a "two closet" solution.
//
// A "closet" in this context is a reference to a coat-check closet at a fancy restaurant,
// where you give the clerk your coat and receive a claim ticket in return.
//
// Language-bridging closets take care of the actual ownership of your c++ object, and return
// you a simple integer "ticket" which can be used to access an object when needed.
//
// A JavaScript application will be given a JavaScript object of type ClosetObject. This is called the "handle" or "wrapper."
// The ClosetObject has an integer claim ticket for the object that it logically owns in the other language's memory pool.
//
// To support event dispatching of events originating in C++, we need a secondary closet to manage JavaScript listener object lookups (hence "two closet").
//
// The second closet is a JavaScript memory pool.
//
//
// Map used to store all ClosetObjects by their closetKey
//
var _closet = new Map();
//
// ID of the timout used for garbage collection
//
var gcTimeoutID = null;
//
// Used to keep track of maximum closet size (while in trace mode).
//
var maxClosetSize = 0;
//
// The secret token used to create ClosetObjects.
//
var __TOKEN__ = {};
export function isToken(token) {
    return __TOKEN__ === token;
}
function runGarbageCollection() {
    gcTimeoutID = null; // reset the timeout id
    var garbageAddresses = [];
    var garbageKeys = [];
    _closet.forEach(function (closetObject, closetKey) {
        if (!closetObject.isRetained()) {
            garbageAddresses.push(closetObject.getClosetAddress()); // keep track of garbage
            garbageKeys.push(closetKey); // For logging
            _closet.delete(closetKey); // remove wrapper from the closet
            //
            // Reset the wrapper's closetAddress to 0 (nullptr), making
            // the object unusable.
            //
            closetObject._resetClosetAddress();
        }
    });
    //
    // Remove the objects from the c++ wrappable closet.
    //
    var releaseCount = garbageAddresses.length;
    if (releaseCount > 0) {
        ltjsModule.releaseAllWrappables(garbageAddresses);
    }
    //
    // print tracing information
    //
    if (traceEnabled) {
        var retainCount = ltjsModule.getWrappableClosetSize();
        console.log('TRACE', "WrappableCloset: Running garbage collection. ".concat(releaseCount, " objects released. ").concat(retainCount, " objects retained."));
        //
        // Print the keys that were released. Needed for tracing tools.
        //
        if (releaseCount) {
            console.log('TRACE', garbageKeys);
        }
        //
        // The primitive closet is a temporary thing, only used for passing arguments
        // and return values, and should always be 0 at this point.
        //
        var primitiveCount = ltjsModule.getPrimitiveClosetSize();
        if (primitiveCount !== 0) {
            console.log('TRACE', "ERROR: The PrimitiveCloset should be empty, but has ".concat(primitiveCount, " items!"));
        }
        var newClosetSize = releaseCount + retainCount;
        if (newClosetSize > maxClosetSize) {
            maxClosetSize = newClosetSize;
            console.log('TRACE', "WrappableCloset size has peaked at ".concat(maxClosetSize, " objects (").concat(retainCount, " retained, ").concat(releaseCount, " temporary)."));
        }
    }
}
// type ClosetObjectClass = typeof ClosetObject;
function convertObjectFromC(closetKey) {
    var closetAddress = ltjsModule.commitStagedObject(closetKey);
    var typeID = ltjsModule.getTypeId(closetAddress);
    if (typeID < 0) {
        throw new Error('Closet object resolved to invalid typeID ' + typeID);
    }
    var ClosetObjectConstructor = getModule(typeID);
    return new ClosetObjectConstructor(__TOKEN__, closetAddress, closetKey);
}
function getModule(typeID) {
    var typeEntry = typeMap.getTypeEntry(typeID);
    if (!typeEntry) {
        throw new Error("Error: typeid ".concat(typeID, " is not a valid type."));
    }
    var ClassConstructor = typeMap.getModuleByName(typeEntry.moduleName);
    if (!ClassConstructor) {
        // The new openUI application builds set window['sas-ui5-build'] to true if used.
        // We check that here so that dynamic module require is only used when using
        // openUI without application builds.
        if (sas.ltjs.isOpenUI && !window['sas-ui5-build'] && !sas.ltjs.webpack) {
            if (!typeEntry.modulePath) {
                // TODO: this used to return null, but now throws.
                // It seems like returning null would have resulted in a crash anyway.
                throw new Error("Error: typeid ".concat(typeID, " does not define a valid module path."));
            }
            // eslint-disable-next-line sashtmlcommons/no-dynamic-import
            ClassConstructor = sas.ltjs.require(typeEntry.modulePath);
        }
        else {
            // Recurse up the module's parent chain until a loaded module
            // is found.  This should always at least resolve as the return type
            // defined on the wrapper's function, because return types are required
            // in the generated wrapper.
            return getModule(typeEntry.parentTypeId);
        }
    }
    return ClassConstructor;
}
export function getSize() {
    return _closet.size;
}
export function getExistingWrapper(closetKey) {
    return _closet.get(closetKey);
}
/**
 * Returns the JavaScript object wrapper for an object within the closet.
 * @param {number} closetKey
 * @returns {sas.ltjs.commons.ClosetObject} wrapper
 */
export function getWrapper(closetKey) {
    //
    // WrappableCloset.getWrapper is used by *jni.js modules to get the ClosetObject
    // return values.  If the closetKey is 0 (nullptr in c++), then null is returned.
    // If there is an existing ClosetObject for that key, then it is returned.
    // Otherwise a new ClosetObject is created.
    //
    //
    // 0 is the nullptr closetKey, so return null.
    //
    if (closetKey === 0) {
        // No real need to discard, but it does perform an assertion that the staged Wrappable is actuall null.
        ltjsModule.discardStagedObject(closetKey);
        return null;
    }
    //
    // Reuse the existing ClosetObject if one exists.
    // Otherwise create one.
    //
    var closetObject = _closet.get(closetKey);
    if (closetObject) {
        //
        // release the Wrappable that is staged in the c++ closet
        //
        ltjsModule.discardStagedObject(closetKey);
    }
    else {
        //
        // create a new ClosetObject to wrap the c++ object.
        // This commits the staged object to the closet, and creates the new ClosetObject.
        // The ClosetObject constructor is responsible for adding itself to the closet
        // by calling WrappableCloset.insertWrapper().
        //
        closetObject = convertObjectFromC(closetKey);
    }
    //
    // Only return a non-null ClosetObject. If the creation failed
    // then fall through to throw the error.
    //
    if (closetObject) {
        return closetObject;
    }
    //
    // This was either an invalid closetKey or there was a problem with creating
    // a new ClosetObject from what was thought to be a good index.
    //
    throw new Error("A ClosetObject could not be constructed for the Wrappable with closetKey ".concat(closetKey));
}
/**
 * Returns the JavaScript object wrapper for an object within the closet.
 * @param {number} closetKey - key for the WrappableVectorPtr in the closet.
 * @returns {sas.ltjs.commons.ClosetObject[]} array of wrappers
 */
export function getWrapperArray(closetKey) {
    //
    // 0 is the nullptr closetKey, so return null.
    //
    if (closetKey === 0) {
        // No real need to discard, but it does perform an assertion that the staged Wrappable is actually null.
        ltjsModule.discardStagedObject(closetKey);
        return null;
    }
    var rc = [];
    var size = ltjsModule.getStagedVectorSize(closetKey);
    for (var i = 0; i < size; i++) {
        var elementClosetKey = ltjsModule.stageObjectAt(i);
        rc[i] = getWrapper(elementClosetKey);
    }
    ltjsModule.discardStagedVector(closetKey);
    return rc;
}
export function insertWrapper(closetObject, closetKey) {
    //
    //  This function is only ever to be called by ClosetObject's constructor
    //
    if (!closetKey || !closetObject || closetObject.isExpired()) {
        throw new Error('Attempting to insert invalid ClosetObject into the closet.');
    }
    //
    // This object should not already be in the closet.
    // Ensure that it isn't.
    //
    if (_closet.has(closetKey)) {
        throw new Error('Attempting to insert ClosetObject with a duplicate closet key.');
    }
    //
    // add to the closet
    //
    _closet.set(closetKey, closetObject);
    //
    // Wrappers are added to the closet as soon as they are created,
    // and all newly create wrappers are not retained and are
    // therefore in need of future garbage collection.
    //
    scheduleGarbageCollection();
}
export function scheduleGarbageCollection() {
    //
    // GC is always performed on a timeout. This ensures that it is run in
    // its own call stack, thus guarantying that local scoped, un-retained,
    // wrappers are never garbage collected until they go out of scope.
    //
    if (gcTimeoutID === null) {
        gcTimeoutID = setTimeout(runGarbageCollection, 0);
    }
}
export function forAllObjectsInCloset(callback) {
    _closet.forEach(callback);
}
export function handleEvent(bridgeClosetKey, eventClosetKey) {
    var bridgeContext = ltjsModule.runtime.dispatchBridgeMap.get(bridgeClosetKey);
    var eventWrapper = bridgeContext === null || bridgeContext === void 0 ? void 0 : bridgeContext.getWrapper(eventClosetKey);
    if (!eventWrapper) {
        console.log('commons_DispatchBridge_handleEvent -- eventClosetKey(' + eventClosetKey + ') is invalid.');
        return;
    }
    var bridge = bridgeContext === null || bridgeContext === void 0 ? void 0 : bridgeContext.getExistingWrapper(bridgeClosetKey);
    if (!bridge) {
        console.log('commons_DispatchBridge_handleEvent -- bridgeClosetKey(' + bridgeClosetKey + ') is invalid.');
        return;
    }
    bridge.handleEvent(eventWrapper);
}
//
// This is ONLY used for unit testing.
//
export function _runGarbageCollection(releaseAll) {
    //
    // If there is already GC scheduled, then unschedule it
    // because we are going to run it NOW.
    //
    if (gcTimeoutID !== null) {
        clearTimeout(gcTimeoutID);
        gcTimeoutID = null;
    }
    //
    // releaseAll parameter forces all closet objects to be released.
    //
    if (releaseAll) {
        _closet.forEach(function (closetObject) {
            while (closetObject.isRetained()) {
                closetObject.release();
            }
        });
    }
    runGarbageCollection();
}
/**
 * Only used for unit testing.
 * @private
 */
export function _releaseAndRemoveAllClosetObjects() {
    // Remove any attached event listeners
    _closet.forEach(function (closetObject) {
        var DispatchBridge = typeMap.getModuleByName('commons::DispatchBridge');
        if (!DispatchBridge)
            throw new Error('Unable to get DispatchBridge module!');
        if (closetObject instanceof DispatchBridge) {
            // tslint:disable-next-line:no-unsafe-any
            ltjsModule.module._ltjs_commons_EventDispatcher_removeEventListener(ltjsModule.module._ltjs_commons_EventDispatcher_ALL(), closetObject.getClosetAddress());
        }
    });
    // Release everything currently in the closet
    _runGarbageCollection(true);
}
